<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <h4 qid="do-page-title">
              {{ dataObject.name }}
            </h4>
            <div class="mb-2">
              <span v-if="getGuide('data_object')">
                <GuideButton :guide="getGuide('data_object')"></GuideButton>
              </span>
            </div>
          </div>

          <div class="col-md-6 text-right" qid="view-do-modified">
            <div class="mb-4">
                <span class="h6 ml-3">
                <span v-if="isGranted([permissions.DATA_OBJECT_EDIT, permissions.DATA_OBJECT_EDIT_OWN], dataObject.created_by) && (!getClient.parent || (getClient.parent && dataObject.partition))"
                      class="btn btn-edit"
                      qid="edit-do-button"
                      @click="showEditDOModal(dataObject)"
                >
                  <font-awesome-icon icon="pen"/>
                </span>
                <span v-if="isGranted([permissions.DATA_OBJECT_DELETE, permissions.DATA_OBJECT_DELETE_OWN], dataObject.created_by) && (!getClient.parent || (getClient.parent && dataObject.partition))"
                      class="btn btn-delete"
                      qid="delete-do-button"
                      @click="showConfirmDeleteModal(dataObject)"
                >
                  <font-awesome-icon icon="trash-alt"/>
                </span>
              </span>
            </div>
            <div>
              <span>{{ $t('table.last_updated') }}</span>
              <span class="ml-2 text-muted small" qid="view-do-modified-date">
              {{ dataObject.updated_at | formatDate }}
            </span>
              <Avatar v-if="dataObject.updated_by" :object="dataObject.updated_by" size="xs" class="ml-3 mr-2"></Avatar>
              <span v-if="dataObject.updated_by" qid="view-do-modified-by">
              {{ dataObject.updated_by.name }}
            </span>
            </div>
          </div>

        </div>

        <hr />
        <div class="content-view-body">
          <br>
          <div class="mt-3">
            <p class="text-dark">
              {{ $t('maintenance.data_objects.create.description_label') }}
            </p>
            <hr />
            <p qid="do-description" class="text-formatted">{{ dataObject.description }}</p>
            <br>
          </div>
          <div>
            <p class="text-dark">
              {{ $t('maintenance.data_objects.create.classification_label') }}
            </p>
            <hr>
            <p qid="do-classification">
              <span v-if="dataObject.classification">
                {{ $t('classification.' + $options.filters.slugify(dataObject.classification)) }}
              </span>
            </p>
            <br>
          </div>
          <div>
            <p class="text-dark">
              {{ $t('maintenance.data_objects.create.business_classification_label') }}
            </p>
            <hr>
            <p qid="do-business-classification">
              <span v-if="dataObject.business_classification">
                {{ $t('classification.' + $options.filters.slugify(dataObject.business_classification)) }}
              </span>
            </p>
            <br>
          </div>
          <div>
            <p class="text-dark">
              {{ $t('maintenance.data_objects.create.category_label') }}
            </p>
            <hr>
            <p qid="do-category">{{ dataObject.category }}</p>
            <br>
          </div>
          <div>
            <p class="text-dark">
              {{ $t('maintenance.data_objects.create.tags_label') }}
            </p>
            <hr>
            <p qid="do-tags">
              <span class="mr-1" v-for="tag in dataObject.tags_translated">
                <span class="badge badge-info mb-1" :title="tag.name_translated">
                  {{tag.name_translated | truncate(35)}}
                </span>
              </span>
            </p>
            <br>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit data object modal template -->
    <b-modal ref="edit-data-object" @hide="hideEditDOModal" modal-class="task-modal" hide-footer>
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideEditDOModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="update-do-title">
            {{ $t('maintenance.data_objects.update.title') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-do-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="editDOFormSubmit" qid="edit-do-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-do-name-label">
              {{ $t('maintenance.data_objects.create.name_label') }}<span class="mandatory"> *</span>
            </label>
            <b-form-input
              qid="edit-do-form-name"
              type="text"
              v-model="doForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.data_objects.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-do-description-label">
              {{ $t('maintenance.data_objects.create.description_label') }}
            </label>
            <b-form-textarea
              qid="edit-do-form-description"
              v-model="doForm.description"
              rows="6"
              max-rows="6"
              :placeholder="$t('maintenance.data_objects.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <label qid="edit-do-classification-label">
              {{ $t('maintenance.data_objects.create.classification_label') }}<span class="mandatory"> *</span>
            </label>
            <multiselect
                v-model="doForm.classification"
                :options="classificationItems"
                :placeholder="$t('maintenance.data_objects.create.classification_placeholder')"
                label="name"
                required
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="edit-do-form-classification"
            ></multiselect>
          </b-form-group>
          <b-form-group>
            <label qid="edit-do-business-classification-label">
              {{ $t('maintenance.data_objects.create.business_classification_label') }}
            </label>
            <multiselect
                v-model="doForm.business_classification"
                :options="businessClassificationItems"
                :placeholder="$t('maintenance.data_objects.create.business_classification_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="edit-do-form-business-classification"
            ></multiselect>
          </b-form-group>
          <b-form-group :class="{'multiselect--show-input': categoryInput.length}">
            <label qid="edit-do-category-label">
              {{ $t('maintenance.data_objects.create.category_label') }}
            </label>
            <multiselect
                v-model="selectedCategory"
                :options="categoryItems"
                :placeholder="$t('maintenance.data_objects.create.category_placeholder')"
                :preserveSearch="true"
                @search-change="handleCategoryInput"
                label="category"
                track-by="category"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                qid="edit-do-form-category"
            >
              <template slot="noResult" slot-scope="props">
                {{ $t('maintenance.data_objects.create.category_no_results') }}
              </template>
            </multiselect>
          </b-form-group>
          <b-form-group>
            <label qid="edit-do-tags-label">
              {{ $t('maintenance.data_objects.create.tags_label') }}
            </label>
            <multiselect
                v-model="selectedTags"
                :options="tags"
                :multiple="true"
                :taggable="true"
                track-by="name_translated"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('maintenance.data_objects.create.tags_placeholder')"
                label="name_translated"
                qid="edit-do-tags-select"
                @search-change="loadTags"
                @tag="addTag"
            ></multiselect>
          </b-form-group>
        </div>
        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideEditDOModal"
                            qid="edit-do-cancel-button"
                  >
                    {{ $t('maintenance.data_objects.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="ml-3"
                            variant="success"
                            :disabled="buttonDisabled"
                            qid="edit-m-submit-button"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('maintenance.data_objects.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </b-modal>


    <!-- Delete data object confirm modal template -->
    <b-modal ref="delete-data-object" modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideConfirmDeleteModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="delete-do-title">
            {{ $t('maintenance.data_objects.delete.title') }}
          </h5>
        </div>
      </template>
      <div class="mb-2 mt-3" style="font-size:18px;">
        <div>
          {{ $t('maintenance.data_objects.delete.description') }}
          <span class="text-bold">
            {{ itemToDelete.name }}
          </span>
        </div>
        <div class="mt-2">
          {{ $t('maintenance.data_objects.delete.are_you_sure') }}
        </div>
      </div>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideConfirmDeleteModal"
                            qid="delete-do-cancel-button"
                  >
                    {{ $t('maintenance.data_objects.delete.cancel') }}
                  </b-button>
                  <b-button @click="confirmDeleteDO(itemToDelete)"
                            class="ml-3"
                            variant="danger"
                            qid="delete-do-submit-button"
                            v-if="isGranted([permissions.DATA_OBJECT_DELETE, permissions.DATA_OBJECT_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
                  >
                    {{ $t('maintenance.data_objects.delete.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import Avatar from '@/components/Avatar.vue';
import GuideButton from '@/components/guide/GuideButton.vue';


export default {
  name: 'dataObjectView',
  data: function() {
    return {
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('maintenance.title'),
          to: '/maintenance'
        },
        {
          text: this.$t('maintenance.data_objects.title'),
          to: '/maintenance/data-objects'
        }
      ],
      dataObject: {},
      showInput: false,
      showSingleLinkLabel: true,
      inputLink: '',
      itemToDelete: {},
      doForm: {
        name: '',
        description: '',
        classification: '',
        business_classification: '',
        category: [],
        tags: [],
        client:'',
      },
      error: '',
      selectedTags: [],
      classificationItems: [
        { name: this.$t('classification.generic_art._6'), value: 'Generic art. 6' },
        { name: this.$t('classification.sensitive_art._9'), value: 'Sensitive art. 9' },
        { name: this.$t('classification.criminal_art._10'), value: 'Criminal art. 10' },
        { name: this.$t('classification.national_identification_number_art._87'), value: 'National identification number art. 87' },
        { name: this.$t('system.n_a'), value: 'N/A' }
      ],
      businessClassificationItems: [
        { name: this.$t('classification.unclassified'), value: 'Unclassified' },
        { name: this.$t('classification.confidential'), value: 'Confidential' },
        { name: this.$t('classification.secret'), value: 'Secret' },
        { name: this.$t('classification.top_secret'), value: 'Top secret' }
      ],
      categoryItems: [],
      categoryInput: '',
      selectedCategory: [],
      tags: [],
      newTags: [],
      buttonDisabled: false,
      myGuides: [],
    }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    }
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug !== oldValue.slug) {
        this.goToList()
      } else {
        this.loadContent();
        this.loadCategories();
        this.loadTags();
      }
    }
  },
  components: {
    Avatar,
    GuideButton
  },
  methods: {
    goToList() {
      this.$router.push('/maintenance/data-objects')
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Maintenance' && object.enabled
      });
      return existingGuide
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Maintenance',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    loadContent() {
      let self = this;
      piincHttp.get('data-objects/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.dataObject = response.data;
        // Push Third party name in breadcrumb array
        self.breadcrumbItems.push({text: self.dataObject.name, active: true});
      }, function() {});
    },
    addTag(newTag) {
      const tag = {
        name: newTag,
        translatable: newTag,
        name_translated: newTag
      }
      this.selectedTags.push(tag)
      this.newTags.push(tag)
    },
    handleCategoryInput(input) {
      this.categoryInput = input;
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return require('@/assets/avatar.png');
      }
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-data-object'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-data-object'].hide()
    },
    confirmDeleteDO(object) {
      let self = this;
      piincHttp.delete('data-objects/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('maintenance.data_objects.delete.success'))
      }, function() {});
    },
    showEditDOModal(object) {
      this.error = '';
      let self = this;
      this.loadCategories();
      this.loadTags();

      // Preselect classification on edit modal
      if (object.classification) {
        this.doForm.classification = _.find(this.classificationItems, function(item) {
          return item.value === object.classification;
        });
      }
      if (object.business_classification) {
        this.doForm.business_classification = _.find(this.businessClassificationItems, function(item) {
          return item.value === object.business_classification;
        });
      }

      // Preselect category on edit modal
      if (object.category) {
        this.selectedCategory = []
        _.find(this.categoryItems, function(item) {
          if (item.category == object.category) {
            self.selectedCategory = item;
          }
        });
      }

      // Set selected countries on edit modal
      if (object.tags_translated && object.tags_translated.length) {
        this.selectedTags = _.cloneDeep(object.tags_translated);
      }

      this.doForm.slug = object.slug;
      this.doForm.name = object.name;
      this.doForm.description = object.description;

      this.$refs['edit-data-object'].show()
    },
    hideEditDOModal() {
      this.categoryInput = ''
      this.newTags = []
      this.$refs['edit-data-object'].hide()
    },
    loadCategories(query) {
      let self = this;
      piincHttp.get('/data-objects/categories', { params:
        {
          client: this.getClient.slug,
          keyword: query ? query : undefined
        }
      }).then(function(response) {
        self.categoryItems = response.data
        if (self.categoryItems && !self.categoryItems.length && !query) {
          self.categoryItems.push({category: self.$t('system.no_results'), $isDisabled: true})
        }
      }, function() {});
    },
    loadTags(query) {
      let self = this;
      piincHttp.get('tags', { params:
        {
          client: this.getClient.slug,
          keyword: query ? query : undefined
        }
      }).then(function(response) {
        self.tags = response.data
        if (self.tags && !self.tags.length && !query) {
          self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
        }
        for (var i = 0; i < self.newTags.length; i++) {
          self.tags.push(self.newTags[i]);
        }
      }, function() {});
    },
    editDOFormSubmit(evt) {
      this.buttonDisabled = true;
      var moment = require('moment');
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();
      this.doForm.client = this.getClient.slug;

      if (this.selectedTags && this.selectedTags.length) {
        this.doForm.tags = [];
        for (var i = 0; i < this.selectedTags.length; i++) {
          this.doForm.tags.push(this.selectedTags[i].name_translated);
        }
      } else {
        this.doForm.tags = [];
      }

      if (this.doForm.name && this.doForm.classification) {
        this.doForm.classification = this.doForm.classification ? this.doForm.classification.value : ''
        this.doForm.business_classification = this.doForm.business_classification ? this.doForm.business_classification.value : ''
      }

      if (this.categoryInput && this.categoryInput.length) {
        this.doForm.category = this.categoryInput
      } else {
        if (this.selectedCategory && this.selectedCategory.category) {
          this.doForm.category = this.selectedCategory.category
        } else {
          this.doForm.category = '';
        }
      }

      piincHttp.put('data-objects/' + this.doForm.slug, this.doForm).then(function(response) {
        self.hideEditDOModal();
        self.buttonDisabled = false;
        // If slug is not changed, just reload content
        if (response.data.slug === self.dataObject.slug) {
          // Remove last item in breadcrumbs, because it will be added again on load content
          self.breadcrumbItems.pop();
          self.loadContent();
        } else {
          // If slug changed, reload state with new param
          self.$router.push({ name: 'maintenance.data_objects.view', params: { slug: response.data.slug }})
        }
        self.loadCategories();
        self.$toastr('success', self.$t('maintenance.data_objects.update.success'))
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          // Attach error if exists
          self.error = ''
          if (error.data.errors.name || error.data.errors.classification) {
            if (error.data.errors.name) {
              self.error += ' ' + self.$t('maintenance.data_objects.create.name_error')
            }
            if (error.data.errors.classification) {
              self.error += ' ' + self.$t('maintenance.data_objects.create.classification_error')
            }
          }else{
            self.error = error.data.message;
          }
        }
      })
    }
  },
  created () {
    if (this.getClient && this.getClient.slug) {
      this.loadContent();
      this.loadCategories();
      this.loadTags();
    }
    this.loadGuides('data_object');
  }
}
</script>
